import { Group, Text, Button, Box, Grid, ActionIcon, Flex, Container, Card, Skeleton, Input, Popover, Stack } from '@mantine/core';
import { IconHelp, IconTrash, IconPencil, IconCheck, IconX, IconSearch, IconArrowsSort } from '@tabler/icons-react';
import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from "react-router-dom";
import { useDocumentTitle } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { HeaderMenu } from "../../components/HeaderMenu/HeaderMenu";
import CampaignReport from "../../api/CampaignReport";
import ReactDOM from 'react-dom';
import { SelectTooltip } from "../../components/GeneralComponent/GeneralComponent";
import PermissionWrapper from '../../PermissionWrapper';

export function CampaignReports() {
  const navigate = useNavigate();
  const [selectedMemberWebsite, setSelectedMemberWebsite] = useState('');
  const [reportList, setReportList] = useState([]);
  const [showSkeleton, setShowSkeleton] = useState(false);

  const resportIconRef = useRef(null);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [helpTargetId, setHelpTargetId] = useState(0);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

  const [sortedReportList, setSortedReportList] = useState([]);
  const [sortModalOpened, setSortModalOpened] = useState(false);
  const [selectedSort, setSelectedSort] = useState(null);
  const [showCreateReportButton, setShowCreateReportButton] = useState(true);

  const handleHeaderMenuComponentChange = (Value:any) => {
    setSelectedMemberWebsite(Value);
  };

  useDocumentTitle("Visiopt - Reports");

  const updateReport = (report_id:any) => {
    navigate(`/campaign-report/${report_id}`);
  }

  const deleteReportConfirm = (deleteReportId:any) => modals.openConfirmModal({
    trapFocus: false,
    title: 'Please confirm your action',
    children: (
      <Text size="sm">
        Are you sure you want to delete this Report?
      </Text>
    ),
    labels: { confirm: 'Confirm', cancel: 'Cancel' },
    onCancel: () => {},
    onConfirm: () => {handleDeleteCampaignReport(deleteReportId)}
  });

  const handleDeleteCampaignReport = (reportId:any) => {
    setShowSkeleton(true);
    CampaignReport.deletereport({
      report_id: reportId
    })
      .then(response => {
        if (response.ok) {
          return response.json();
        } else {
          if (response.status === 401 || response.status === 400) {
            navigate("/logout");
          }
        }
      })
      .then(data => {
        if(data.status === 1){
          notifications.show({
            color: 'green',
            icon: <IconCheck size="1.1rem" />,
            title: 'Success',
            message: data.message,
          });
          fetchCampaignReportList();
          setShowSkeleton(false);
        }else{
          notifications.show({
            color: 'red',
            icon: <IconX size="1.1rem" />,
            title: 'Error',
            message: data.message,
          });
          setShowSkeleton(false);
        }
      })
      .catch(error => {
        console.error("API Error:", error);
        setShowSkeleton(false);
      });
  };

  const fetchCampaignReportList = () => {
    setShowSkeleton(true);
    CampaignReport.getreportlist()
      .then(response => {
        if (response.ok) {
          return response.json();
        } else {
          if (response.status === 401 || response.status === 400) {
            navigate("/logout");
          }
        }
      })
      .then(data => {
        if(data.status === 1){
          setReportList(data.data.report_list);
          setSortedReportList(data.data.report_list);
          setShowSkeleton(false);
          setShowCreateReportButton(data.data.show_create_report_button);
        }else{
          notifications.show({
            color: 'red',
            icon: <IconX size="1.1rem" />,
            title: 'Error',
            message: data.message,
          });
          setShowSkeleton(false);
        }
      })
      .catch(error => {
        console.error("API Error:", error);
        setShowSkeleton(false);
      });
  };

  useEffect(() => {
    if(selectedMemberWebsite !== ''){
      fetchCampaignReportList();
    }
  }, [selectedMemberWebsite]);

  const handleHelpClick = (id:any) => {
    setShowHelpModal(true);
    setHelpTargetId(id);
    if (resportIconRef.current) {
      const { top, left, width } = resportIconRef.current.getBoundingClientRect();
      const scrollOffset = window.scrollY;
      setTooltipPosition({
        top: top + scrollOffset,
        left: left + width + 10, 
      });
    }
  };

  const handleKeywordChange = (e) => {
    const keyword = e.target.value;

    const filteredList = reportList.filter(report =>
      report.report_name?.toLowerCase().includes(keyword.toLowerCase())
    );

    setSortedReportList(filteredList);
  };

  const handleSort = (option) => {
    setSortModalOpened(false);
    setShowSkeleton(true);
    setSelectedSort(option);
    CampaignReport.getreportlist({
      sorting_type: option
    })
    .then(response => {
      if (response.ok) {
        return response.json();
      } else {
        if (response.status === 401 || response.status === 400) {
          navigate("/logout");
        }
      }
    })
    .then(data => {
      if(data.status === 1){
        setReportList(data.data.report_list);
        setSortedReportList(data.data.report_list);
        setShowSkeleton(false);
        setShowCreateReportButton(data.data.show_create_report_button);
      }else{
        notifications.show({
          color: 'red',
          icon: <IconX size="1.1rem" />,
          title: 'Error',
          message: data.message,
        });
        setShowSkeleton(false);
      }
    })
    .catch(error => {
      console.error("API Error:", error);
      setShowSkeleton(false);
    });
  };

	return (
		<>
    {showHelpModal &&
        ReactDOM.createPortal(
          <div>
            <SelectTooltip
              isVisible={showHelpModal}
              setIsVisible={setShowHelpModal}
              id={helpTargetId}
              tooltipPosition={tooltipPosition} 
            />
          </div>,
        document.body
    )}
    {showHelpModal && <SelectTooltip isVisible={showHelpModal} setIsVisible={setShowHelpModal} id={helpTargetId} tooltipPosition={tooltipPosition} />}

    <HeaderMenu HeaderMenuValue={selectedMemberWebsite} HeaderMenuOnChange={handleHeaderMenuComponentChange}/>
    <Container size="xl">
      <Card shadow="sm" padding="sm" radius="md" withBorder>
  			<Group position="apart" mb="xs">
          <Text weight={500}>REPORTS <IconHelp ref={resportIconRef} size="1.1rem" color="red" style={{marginBottom:-3, cursor: 'pointer'}} onClick={() => handleHelpClick(353)}/></Text>
        </Group>
        {showCreateReportButton && (
          <PermissionWrapper permission="operational_access">
          <Button type="button">
            <Link to="/campaign-report/new" style={{color:"white",textDecoration: "none"}}>
              Create a New Report
            </Link>
          </Button>
          </PermissionWrapper>
        )}
        {reportList.length > 0 && (
          <Box
            p={4}
            pl={20}
            mt={10}
            sx={(theme) => ({
              padding: "10px",
              borderRadius: theme.radius.md,
              fontWeight: "bold",
            })}
          >
          {reportList.some(item => Array.isArray(item) ? item.length > 0 : true) && (
            <Flex justify="space-between" align="center" style={{ width: "100%" }}>
              <Popover opened={sortModalOpened} onChange={setSortModalOpened} position="bottom-start" shadow="md" width={150} withinPortal>
              <Popover.Target>
                <Text
                  mt={3}
                  weight={500}
                  onClick={() => setSortModalOpened((o) => !o)}
                  style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
                >
                  <Group spacing={5} align="center">
                    <span style={{ fontSize: 18 }}>Sort</span>
                    <IconArrowsSort stroke={1.5}/>
                  </Group>
                </Text>
              </Popover.Target>
              <Popover.Dropdown>
                <Stack spacing={5}>
                  <Button variant={selectedSort === 'az' ? 'filled' : 'subtle'} fullWidth onClick={() => handleSort('az')}>A – Z</Button>
                  <Button variant={selectedSort === 'za' ? 'filled' : 'subtle'} fullWidth onClick={() => handleSort('za')}>Z – A</Button>
                  <Button variant={selectedSort === 'oldest' ? 'filled' : 'subtle'} fullWidth onClick={() => handleSort('oldest')}>Oldest</Button>
                  <Button variant={selectedSort === 'newest' ? 'filled' : 'subtle'} fullWidth onClick={() => handleSort('newest')}>Newest</Button>
                </Stack>
              </Popover.Dropdown>
            </Popover>
              <Input icon={<IconSearch size={16} />} 
                placeholder="Search"
                maxLength={50}
                autoFocus
                onChange={handleKeywordChange}
                style={{ maxWidth: "250px" }}
              />
            </Flex>
          )}
          </Box>
        )}
        {showSkeleton ? (
          <Flex direction="column" gap="sm" mt={10}>
            {Array.from({ length: 12 }).map((_, index) => (
              <Skeleton key={index} height={44} width={1262} />
            ))}
          </Flex>
        ) : (
          sortedReportList.map((Report, ReportIndex) => (
            <Box
              key={ReportIndex}
              p={4}
              pl={20}
              mt={10}
              sx={(theme) => ({
                backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : '#f8f9fa',
                padding: "10px",
                borderRadius: theme.radius.md,
                border: "0.0625rem solid transparent",
                borderColor: theme.colorScheme === 'dark' ? theme.colors.dark[1] : '#dee2e6'
              })}
            >
              <Grid gutter="xs">
                <Grid.Col span={6}>
                  <Text 
                    mt={3} 
                    onClick={() => updateReport(Report.report_id)}
                    style={{ cursor: "pointer" }}
                  >
                    {Report.report_name}
                  </Text>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Flex
                    justify="flex-end"
                    align="center"
                    direction="row"
                    wrap="wrap"
                  >
                  <PermissionWrapper permission="operational_access">
                  <ActionIcon
                    variant="default"
                    size="lg"
                    mx={3}
                    title="Edit"
                    onClick={() => updateReport(Report.report_id)}
                  >
                    <IconPencil size="1.1rem" />
                  </ActionIcon>
                  <ActionIcon
                    variant="default"
                    size="lg"
                    mx={3}
                    title="Delete"
                    onClick={() => deleteReportConfirm(Report.report_id)}
                  >
                    <IconTrash size="1.1rem" />
                  </ActionIcon>
                  </PermissionWrapper>
                  </Flex>
                </Grid.Col>
              </Grid>
            </Box>
          ))
        )}
      </Card>
    </Container>
		</>
	);
}